import { BreakpointsOptions, createTheme } from '@mui/material/styles';

import { componentsOverride, DEFAULT_FONT } from './components';

export type ColorOption = {
  light: string;
  main: string;
  dark: string;
};

export type ColorOptionWithContrast = ColorOption & {
  contrastText: string;
};

export type ColorPalette = {
  primary: ColorOptionWithContrast;
  secondary: ColorOptionWithContrast;
  warning: ColorOption;
  info: ColorOptionWithContrast;
  error: ColorOption;
  success: ColorOption;
  grey: {
    '50': string;
    '100': string;
    '200': string;
    '300': string;
    '400': string;
    '500': string;
    '600': string;
    '700': string;
    '800': string;
    '900': string;
  };
  common: {
    black: string;
    white: string;
  };
};

export type ColorType = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

type ThemeOptions = {
  palette: ColorPalette;
  breakpoints?: BreakpointsOptions;
  font?: string;
};

// Lighter than MUI's default elevation ramp — default elevation 1 is
// `0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)`,
// which reads as too heavy for flat Card-based dashboard layouts.
const LIGHT_SHADOWS = [
  'none',
  '0px 1px 2px 0px rgba(0,0,0,0.06)',
  '0px 1px 3px 0px rgba(0,0,0,0.07)',
  '0px 1px 4px 0px rgba(0,0,0,0.07)',
  '0px 2px 4px 0px rgba(0,0,0,0.07)',
  '0px 2px 6px 0px rgba(0,0,0,0.08)',
  '0px 3px 6px 0px rgba(0,0,0,0.08)',
  '0px 3px 8px 0px rgba(0,0,0,0.08)',
  '0px 4px 8px 0px rgba(0,0,0,0.09)',
  '0px 4px 10px 0px rgba(0,0,0,0.09)',
  '0px 5px 10px 0px rgba(0,0,0,0.09)',
  '0px 5px 12px 0px rgba(0,0,0,0.1)',
  '0px 6px 12px 0px rgba(0,0,0,0.1)',
  '0px 6px 14px 0px rgba(0,0,0,0.1)',
  '0px 7px 14px 0px rgba(0,0,0,0.1)',
  '0px 7px 16px 0px rgba(0,0,0,0.11)',
  '0px 8px 16px 0px rgba(0,0,0,0.11)',
  '0px 8px 18px 0px rgba(0,0,0,0.11)',
  '0px 9px 18px 0px rgba(0,0,0,0.11)',
  '0px 9px 20px 0px rgba(0,0,0,0.12)',
  '0px 10px 20px 0px rgba(0,0,0,0.12)',
  '0px 10px 22px 0px rgba(0,0,0,0.12)',
  '0px 11px 22px 0px rgba(0,0,0,0.12)',
  '0px 11px 24px 0px rgba(0,0,0,0.13)',
  '0px 12px 24px 0px rgba(0,0,0,0.13)',
] as const;

export const getTheme = ({ palette, breakpoints, font }: ThemeOptions) =>
  createTheme({
    palette,
    components: componentsOverride(palette, font),
    typography: {
      fontFamily: `${font || DEFAULT_FONT}, Arial, sans-serif`,
    },
    breakpoints,
    shadows: LIGHT_SHADOWS as unknown as ReturnType<typeof createTheme>['shadows'],
  });
